import React, { useEffect, useMemo, useState } from 'react';
import { Gift, RefreshCw } from 'lucide-react';
import { database, ref, get } from '../../firebase';
import { fetchUserScratchHistory } from '../../utils/scratchCardService';

const formatAmount = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const formatDateTime = (value, fallbackDate) => {
  const raw = value || fallbackDate;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return fallbackDate || 'N/A';
  return d.toLocaleString();
};

const fetchLegacyScratchEntries = async ({ userId, memberId, sponsorId }) => {
  const snap = await get(ref(database, 'UserFundTransactions'));
  if (!snap.exists()) return [];

  const ids = new Set([userId, memberId, sponsorId].map((item) => String(item || '').trim()).filter(Boolean));
  const rows = [];
  const data = snap.val();

  Object.entries(data).forEach(([dateKey, users]) => {
    if (!users || typeof users !== 'object') return;
    Object.entries(users).forEach(([userKey, txs]) => {
      if (!txs || typeof txs !== 'object') return;
      Object.entries(txs).forEach(([id, tx]) => {
        if (!tx || tx.type !== 'scratch_reward') return;
        const match =
          ids.has(String(userKey || '').trim()) ||
          ids.has(String(tx.userId || '').trim()) ||
          ids.has(String(tx.memberId || '').trim()) ||
          ids.has(String(tx.sponsorId || '').trim());
        if (!match) return;

        rows.push({
          id,
          dateKey,
          timestamp: tx.timestamp,
          timestampFormatted: tx.timestampFormatted,
          amount: Number(tx.amount) || 0,
          eventType: String(tx.description || '').toLowerCase().includes('package') ? 'package_activation' : 'fund_add',
          description: tx.description || 'Scratch reward',
          wallet: tx.wallet || 'Fund Wallet',
          referenceId: tx.referenceId || '',
          source: 'UserFundTransactions'
        });
      });
    });
  });

  return rows;
};

const ScratchCardHistory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem('userId') || '';
  const memberId = localStorage.getItem('dbKey') || '';
  const sponsorId = localStorage.getItem('userSponsorId') || userId;
  const userName = localStorage.getItem('userName') || '';

  const loadHistory = async () => {
    setLoading(true);
    try {
      const [newScratchList, legacyList] = await Promise.all([
        fetchUserScratchHistory({
          userId,
          memberId,
          sponsorId,
          userName
        }),
        fetchLegacyScratchEntries({ userId, memberId, sponsorId })
      ]);

      const merged = [...newScratchList];
      legacyList.forEach((item) => {
        const exists = merged.some(
          (row) =>
            String(row.referenceId || '') === String(item.referenceId || '') &&
            String(row.eventType || '') === String(item.eventType || '') &&
            Number(row.amount || 0) === Number(item.amount || 0)
        );
        if (!exists) merged.push(item);
      });

      merged.sort((a, b) => {
        const tA = new Date(a.timestampFormatted || a.timestamp || 0).getTime() || 0;
        const tB = new Date(b.timestampFormatted || b.timestamp || 0).getTime() || 0;
        return tB - tA;
      });

      setRows(merged);
    } catch (error) {
      console.error('Error fetching scratch card history:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const totalAmount = useMemo(
    () => rows.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [rows]
  );

  return (
    <div className="min-h-[70vh] p-2 md:p-4">
      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
              <Gift size={22} className="text-yellow-400" />
              Scratch Card History
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Total Claimed: <span className="text-green-400 font-semibold">{formatAmount(totalAmount)}</span> | Claims: <span className="text-white">{rows.length}</span>
            </p>
          </div>
          <button
            onClick={loadHistory}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading scratch history...</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No scratch claims found</div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-300">Date</th>
                    <th className="text-left px-4 py-3 text-gray-300">Type</th>
                    <th className="text-left px-4 py-3 text-gray-300">Description</th>
                    <th className="text-left px-4 py-3 text-gray-300">Wallet</th>
                    <th className="text-right px-4 py-3 text-gray-300">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={`${item.dateKey}-${item.id}`} className="border-t border-border">
                      <td className="px-4 py-3 text-white">{formatDateTime(item.timestampFormatted || item.timestamp, item.dateKey)}</td>
                      <td className="px-4 py-3 text-cyan-300">{item.eventType || 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-300">{item.description || 'Scratch reward'}</td>
                      <td className="px-4 py-3 text-gray-300">{item.wallet || 'Fund Wallet'}</td>
                      <td className="px-4 py-3 text-right text-green-400 font-semibold">{formatAmount(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {rows.map((item) => (
                <div key={`${item.dateKey}-${item.id}`} className="rounded-lg border border-border p-3 bg-secondary/20">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400">{formatDateTime(item.timestampFormatted || item.timestamp, item.dateKey)}</div>
                    <div className="text-green-400 font-bold">{formatAmount(item.amount)}</div>
                  </div>
                  <div className="mt-2 text-sm text-cyan-300">{item.eventType || 'N/A'}</div>
                  <div className="text-sm text-gray-300 mt-1">{item.description || 'Scratch reward'}</div>
                  <div className="text-xs text-gray-400 mt-2">Wallet: {item.wallet || 'Fund Wallet'}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScratchCardHistory;
